import React from 'react';
import { useNavigate, useParams, Navigate, Link } from 'react-router-dom';
import { Card, Text } from '@shopify/polaris';
import { ArrowLeftIcon, CashDollarIcon, OrderIcon, SettingsIcon } from '@shopify/polaris-icons';
import { useAuth } from '../AuthComponent';
import { Ic } from '../ui';
import PricingHubSettings from '../AmazonPricingHub/SettingsSection';
import PurchaseOrdersSettings from './PurchaseOrdersSettings';

// Settings is laid out like the Dashboard: a tile per module that has settings
// (plus General for Hub-wide ones), each opening its own page at
// /settings/<id>. The page needs the `settings` permission; a module's tile
// also needs that module's permission (admins get all). A module adds itself
// here (entry + component).
const SECTIONS = [
  {
    id: 'general',
    title: 'General',
    description: 'Hub-wide settings',
    icon: SettingsIcon,
    tool: null,                         // everyone who can see Settings
    Component: GeneralSettings,
  },
  {
    id: 'purchase-orders',
    title: 'Purchase Orders',
    description: 'The Shopify admin session used to read purchase orders and print pick sheets',
    icon: OrderIcon,
    tool: 'purchase-orders',
    Component: PurchaseOrdersSettings,
  },
  {
    id: 'pricing-hub',
    title: 'Pricing Hub',
    description: 'Amazon stores, their syncs, and the Repricer pull',
    icon: CashDollarIcon,
    tool: 'amazon-pricing-hub',
    Component: PricingHubSettings,
  },
];

function GeneralSettings() {
  return (
    <Card>
      <Text as="p" tone="subdued">No Hub-wide settings yet. Each module's settings are on its own page.</Text>
    </Card>
  );
}

// Two keys: `settings` (or admin) opens the page at all; a module's section
// also needs that module's permission.
export const canOpenSettings = (permissions) => {
  const has = (id) => (permissions || []).includes(id);
  return has('admin') || has('settings');
};
const usable = (permissions) => {
  const has = (id) => (permissions || []).includes(id);
  if (!canOpenSettings(permissions)) return [];
  return SECTIONS.filter((s) => s.tool === null || has('admin') || has(s.tool));
};

export default function Settings() {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  if (!canOpenSettings(permissions)) return <Navigate to="/" replace />;
  const sections = usable(permissions);
  return (
    <div className="uh uh-page">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Settings</h1>
          <div className="uh-sub">Pick a module to change its settings.</div>
        </div>
      </div>
      <div className="uh-tools">
        {sections.map((s) => (
          <button type="button" className="uh-tool" key={s.id} onClick={() => navigate(`/settings/${s.id}`)}>
            <span className="ti"><Ic source={s.icon} /></span>
            <span>
              <span className="tn">{s.title}</span>
              <span className="td">{s.description}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// One module's settings page: /settings/<id>.
export function SettingsSection() {
  const { section } = useParams();
  const { permissions } = useAuth();
  const s = usable(permissions).find((x) => x.id === section);
  if (!s) return <Navigate to="/settings" replace />;
  const { Component } = s;
  return (
    <div className="uh uh-page">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">{s.title} settings</h1>
          <div className="uh-sub">{s.description}</div>
        </div>
        <div className="uh-ph-acts">
          <Link className="uh-btn" to="/settings"><Ic source={ArrowLeftIcon} sm />All settings</Link>
        </div>
      </div>
      <Component />
    </div>
  );
}
